import baseQuery from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

export const compassPortalApi = createApi({
    reducerPath: 'compassPortalApi',
    baseQuery: baseQuery,
    tagTypes: ['PortalProjects', 'PortalGroups', 'PortalSources', 'PortalComments', 'PortalSourceComments', 'PortalFileVersions', 'PortalTemplates', 'PortalRuleSets'],
    refetchOnFocus: true,
    endpoints: (builder) => ({

        // ── Clients ───────────────────────────────────────

        getAccessibleClients: builder.query({
            query: () => `compass_portal/clients`,
        }),

        // ── Projects ─────────────────────────────────────

        getMyPortalProjects: builder.query({
            query: () => `compass_portal/projects/my_projects`,
            providesTags: ['PortalProjects'],
        }),
        getPortalProjects: builder.query({
            query: ({ clientName }) => `compass_portal/projects/by_client/${clientName}`,
            providesTags: ['PortalProjects'],
        }),
        getPortalProject: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}`,
            providesTags: ['PortalProjects'],
        }),
        createPortalProject: builder.mutation({
            query: (body) => ({
                url: 'compass_portal/projects',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        updatePortalProject: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        deletePortalProject: builder.mutation({
            query: ({ projectId }) => ({
                url: `compass_portal/projects/${projectId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        updateStageStatus: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/stage_status`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),

        // ── Data Groups ──────────────────────────────────

        getDataGroups: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/groups`,
            providesTags: ['PortalGroups'],
        }),
        createDataGroup: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PortalGroups'],
        }),
        updateDataGroup: builder.mutation({
            query: ({ projectId, groupId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalGroups'],
        }),
        deleteDataGroup: builder.mutation({
            query: ({ projectId, groupId }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalGroups', 'PortalSources'],
        }),
        signOffGroup: builder.mutation({
            query: ({ projectId, groupId, signOff = true }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/sign_off?sign_off=${signOff}`,
                method: 'PUT',
            }),
            invalidatesTags: ['PortalGroups'],
        }),

        // ── Data Sources ─────────────────────────────────

        getDataSources: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/sources`,
            providesTags: ['PortalSources'],
        }),
        createDataSource: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/sources`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['PortalSources'],
        }),
        updateDataSource: builder.mutation({
            query: ({ projectId, sourceId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalSources'],
        }),
        deleteDataSource: builder.mutation({
            query: ({ projectId, sourceId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalSources'],
        }),
        toggleSourceField: builder.mutation({
            query: ({ projectId, sourceId, field }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/toggle`,
                method: 'PUT',
                body: { field },
            }),
            invalidatesTags: ['PortalSources'],
        }),
        updateProcessingIssue: builder.mutation({
            query: ({ projectId, sourceId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/processing_issue`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalSources'],
        }),

        // ── File Inspection / Upload / Download ──────────

        inspectFile: builder.mutation({
            query: ({ projectId, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `compass_portal/projects/${projectId}/inspect_file`,
                    method: 'POST',
                    body: formData,
                };
            },
        }),
        uploadPortalFile: builder.mutation({
            query: ({ projectId, sourceId, file }) => {
                const formData = new FormData();
                formData.append('source_id', sourceId);
                formData.append('file', file);
                return {
                    url: `compass_portal/projects/${projectId}/upload_file`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['PortalSources', 'PortalFileVersions'],
        }),
        getFileDownloadUrl: builder.query({
            query: ({ projectId, sourceId }) =>
                `compass_portal/projects/${projectId}/sources/${sourceId}/download`,
        }),

        // ── Comments ─────────────────────────────────────

        getComments: builder.query({
            query: ({ projectId, groupId }) =>
                `compass_portal/projects/${projectId}/groups/${groupId}/comments`,
            providesTags: ['PortalComments'],
        }),
        addComment: builder.mutation({
            query: ({ projectId, groupId, text }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalComments'],
        }),
        deleteComment: builder.mutation({
            query: ({ projectId, groupId, commentId }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalComments'],
        }),
        addReply: builder.mutation({
            query: ({ projectId, groupId, commentId, text }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments/${commentId}/replies`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalComments'],
        }),
        deleteReply: builder.mutation({
            query: ({ projectId, groupId, commentId, replyId }) => ({
                url: `compass_portal/projects/${projectId}/groups/${groupId}/comments/${commentId}/replies/${replyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalComments'],
        }),

        // ── File Versions ────────────────────────────────

        getFileVersions: builder.query({
            query: ({ projectId, sourceId }) =>
                `compass_portal/projects/${projectId}/sources/${sourceId}/file_versions`,
            providesTags: ['PortalFileVersions'],
        }),

        // ── Processed Files ─────────────────────────────

        uploadProcessedFile: builder.mutation({
            query: ({ projectId, sourceId, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `compass_portal/projects/${projectId}/sources/${sourceId}/upload_processed`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['PortalSources', 'PortalFileVersions'],
        }),
        processSource: builder.mutation({
            extraOptions: { maxRetries: 0 }, // side-effect call — never blind-retry
            // start the process JOB (202) — status/progress lives on the source
            query: ({ projectId, sourceId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/process_start`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalSources', 'PortalFileVersions'],
        }),
        processAll: builder.mutation({
            extraOptions: { maxRetries: 0 },
            query: ({ projectId }) => ({
                url: `compass_portal/projects/${projectId}/process_all`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalSources'],
        }),

        // ── Project templates (N3 / CHA-125) ─────────────
        getTemplates: builder.query({
            query: () => `compass_portal/templates`,
            providesTags: ['PortalTemplates'],
        }),
        saveTemplate: builder.mutation({
            query: ({ projectId, name }) => ({
                url: `compass_portal/templates/from_project/${projectId}`,
                method: 'POST',
                body: { name },
            }),
            invalidatesTags: ['PortalTemplates'],
        }),
        applyTemplate: builder.mutation({
            query: ({ projectId, templateId }) => ({
                url: `compass_portal/projects/${projectId}/apply_template/${templateId}`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalGroups', 'PortalSources'],
        }),

        // ── Source Comments ─────────────────────────────

        getSourceComments: builder.query({
            query: ({ projectId, sourceId }) =>
                `compass_portal/projects/${projectId}/sources/${sourceId}/comments`,
            providesTags: ['PortalSourceComments'],
        }),
        addSourceComment: builder.mutation({
            query: ({ projectId, sourceId, text }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
        deleteSourceComment: builder.mutation({
            query: ({ projectId, sourceId, commentId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
        addSourceReply: builder.mutation({
            query: ({ projectId, sourceId, commentId, text }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments/${commentId}/replies`,
                method: 'POST',
                body: { text },
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),
        deleteSourceReply: builder.mutation({
            query: ({ projectId, sourceId, commentId, replyId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/comments/${commentId}/replies/${replyId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalSourceComments'],
        }),

        // ── Stage deadlines (cascade — later stages shift by the same delta) ──

        setStageDue: builder.mutation({
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/stage_due`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['PortalProjects'],
        }),

        // ── Warehouse-backed sources (CHA-366) ──────────

        pullFromWarehouse: builder.mutation({
            query: ({ projectId, sourceId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/pull_from_warehouse`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalSources'],
        }),
        pullAllFromWarehouse: builder.mutation({
            query: ({ projectId }) => ({
                url: `compass_portal/projects/${projectId}/pull_all_from_warehouse`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalSources'],
        }),

        // ── Requirements template (COmpass template auto-fill) ──

        getRequirementsTemplate: builder.query({
            query: () => `compass_portal/requirements_template`,
        }),

        // ── Rule sets (the per-source processing recipes) ──

        getRuleSets: builder.query({
            query: ({ clientName }) => `compass_portal/rule_sets/${encodeURIComponent(clientName)}`,
            providesTags: ['PortalRuleSets'],
        }),
        getRuleSetVersion: builder.query({
            query: ({ clientName, ruleId, version = 'latest' }) =>
                `compass_portal/rule_sets/${encodeURIComponent(clientName)}/${ruleId}/${version}`,
            providesTags: ['PortalRuleSets'],
        }),
        saveRuleSetDraft: builder.mutation({
            query: ({ clientName, ruleSet }) => {
                const formData = new FormData();
                formData.append('rule_set_json', JSON.stringify(ruleSet));
                return { url: `compass_portal/rule_sets/${encodeURIComponent(clientName)}/draft`, method: 'POST', body: formData };
            },
            invalidatesTags: ['PortalRuleSets'],
        }),
        confirmRuleSet: builder.mutation({
            extraOptions: { maxRetries: 0 }, // LLM/side-effect call — never blind-retry
            // The human gate: records who signed the drafted rules off.
            query: ({ clientName, ruleId, version }) => ({
                url: `compass_portal/rule_sets/${encodeURIComponent(clientName)}/${ruleId}/${version}/confirm`,
                method: 'PUT',
            }),
            invalidatesTags: ['PortalRuleSets', 'PortalSources'],
        }),
        deleteRuleSet: builder.mutation({
            query: ({ clientName, ruleId }) => ({
                url: `compass_portal/rule_sets/${encodeURIComponent(clientName)}/${ruleId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['PortalRuleSets', 'PortalSources'],
        }),
        authorRuleSet: builder.mutation({
            extraOptions: { maxRetries: 0 }, // LLM/side-effect call — never blind-retry
            // Model B: Bravo drafts this source's rules from its uploaded file.
            // Returns a DRAFT ({rule_id, version, summary, rule_set}) assigned to
            // the source; nothing runs until a human confirms.
            query: ({ projectId, sourceId }) => ({
                url: `compass_portal/projects/${projectId}/sources/${sourceId}/author_rule_set`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalRuleSets', 'PortalSources'],
        }),

        // ── Bravo conversation (operator: propose-then-approve) ──
        // POST body is {text, stage, author?} for a message, or {approve, stage,
        // author?} to execute an approved proposal server-side. Response is
        // {messages, proposal|null}; components keep messages in local state from
        // the response, so no cache tags here.

        getConversation: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/conversation`,
        }),
        getModelInputOptions: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/model_input_options`,
            providesTags: ['PortalSources'],
        }),
        startModelFit: builder.mutation({
            extraOptions: { maxRetries: 0 },
            query: ({ projectId, targetColumn, adstockRate = 0.5, grain = 'platform' }) => ({
                url: `compass_portal/projects/${projectId}/model_fit_start?target_column=${encodeURIComponent(targetColumn)}&adstock_rate=${adstockRate}&grain=${grain}`,
                method: 'POST',
            }),
        }),
        getModelResults: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/model_results`,
        }),
        getModelChart: builder.query({
            query: ({ projectId }) => `compass_portal/projects/${projectId}/model_chart`,
        }),
        deliverProject: builder.mutation({
            extraOptions: { maxRetries: 0 },
            query: ({ projectId }) => ({
                url: `compass_portal/projects/${projectId}/deliver`,
                method: 'POST',
            }),
            invalidatesTags: ['PortalProjects'],
        }),
        getValidationSummary: builder.query({
            // Processed spend by channel + computed spike/drop anomalies (Tobi).
            query: ({ projectId }) => `compass_portal/projects/${projectId}/validation_summary`,
        }),
        getValidationChart: builder.query({
            // Backend-supplied Plotly figure ({plot}) for PlotContainer.
            query: ({ projectId }) => `compass_portal/projects/${projectId}/validation_chart`,
        }),
        sendConversationMessage: builder.mutation({
            extraOptions: { maxRetries: 0 }, // LLM/side-effect call — never blind-retry
            query: ({ projectId, ...body }) => ({
                url: `compass_portal/projects/${projectId}/conversation/message`,
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    // Clients
    useGetAccessibleClientsQuery,
    // Projects
    useGetMyPortalProjectsQuery,
    useGetPortalProjectsQuery,
    useGetPortalProjectQuery,
    useCreatePortalProjectMutation,
    useUpdatePortalProjectMutation,
    useDeletePortalProjectMutation,
    useUpdateStageStatusMutation,
    // Groups
    useGetDataGroupsQuery,
    useCreateDataGroupMutation,
    useUpdateDataGroupMutation,
    useDeleteDataGroupMutation,
    useSignOffGroupMutation,
    // Sources
    useGetDataSourcesQuery,
    useCreateDataSourceMutation,
    useUpdateDataSourceMutation,
    useDeleteDataSourceMutation,
    useToggleSourceFieldMutation,
    useUpdateProcessingIssueMutation,
    // Files
    useInspectFileMutation,
    useUploadPortalFileMutation,
    useGetFileDownloadUrlQuery,
    useLazyGetFileDownloadUrlQuery,
    // Comments (group-level)
    useGetCommentsQuery,
    useAddCommentMutation,
    useDeleteCommentMutation,
    useAddReplyMutation,
    useDeleteReplyMutation,
    // File versions
    useGetFileVersionsQuery,
    // Processed files
    useUploadProcessedFileMutation,
    useProcessSourceMutation,
    useProcessAllMutation,
    useGetTemplatesQuery,
    useSaveTemplateMutation,
    useApplyTemplateMutation,
    // Source comments
    useGetSourceCommentsQuery,
    useAddSourceCommentMutation,
    useDeleteSourceCommentMutation,
    useAddSourceReplyMutation,
    useDeleteSourceReplyMutation,
    // Stage deadlines
    useSetStageDueMutation,
    // Warehouse pull
    usePullFromWarehouseMutation,
    usePullAllFromWarehouseMutation,
    // Requirements template
    useGetRequirementsTemplateQuery,
    // Rule sets
    useGetRuleSetsQuery,
    useGetRuleSetVersionQuery,
    useSaveRuleSetDraftMutation,
    useConfirmRuleSetMutation,
    useDeleteRuleSetMutation,
    useAuthorRuleSetMutation,
    // Bravo conversation
    useGetConversationQuery,
    useSendConversationMessageMutation,
    useGetModelInputOptionsQuery,
    useStartModelFitMutation,
    useGetModelResultsQuery,
    useGetModelChartQuery,
    useDeliverProjectMutation,
    useGetValidationSummaryQuery,
    useGetValidationChartQuery,
} = compassPortalApi;

export default compassPortalApi.reducer;
